import { Box, makeStyles, Typography } from '@material-ui/core';
import * as React from 'react';

const useStyles = makeStyles((theme) => ({
  summary: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
  },
  section: {
    display: 'grid',
    gridTemplateColumns: '120px minmax(0, 1fr)',
    columnGap: theme.spacing(1.25),
    rowGap: theme.spacing(0.25),
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
    },
  },
  label: {
    fontSize: 11,
    fontWeight: 700,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  text: {
    lineHeight: 1.55,
  },
}));

export interface ProfilePreviewSection {
  label?: string;
  text: string;
}

const PROFILE_PREVIEW_LABELS = [
  'Current role',
  'Experience level',
  'Level of responsibility',
  'Previous',
  'Total experience',
  'Education',
  'Domain',
];

export function parseProfilePreviewSections(
  text?: string | null,
): ProfilePreviewSection[] {
  if (!text) return [];

  const labelPattern = PROFILE_PREVIEW_LABELS.join('|');
  const normalised = text
    .replace(new RegExp(`\\s*(${labelPattern}):\\s*`, 'g'), '\n$1: ')
    .trim();

  return normalised
    .split('\n')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const match = part.match(new RegExp(`^(${labelPattern}):\\s*(.*)$`));
      if (!match) return { text: part };

      return {
        label: match[1],
        text: match[2].trim(),
      };
    })
    .filter((section) => section.text.length > 0);
}

export function getKeyProfileSections(
  sections: ProfilePreviewSection[],
): ProfilePreviewSection[] {
  let previousIncluded = false;
  const wanted = new Set([
    'Current role',
    'Experience level',
    'Level of responsibility',
    'Total experience',
    'Education',
  ]);

  return sections.filter((section) => {
    if (section.label === 'Previous') {
      if (previousIncluded) return false;
      previousIncluded = true;
      return true;
    }

    return Boolean(section.label && wanted.has(section.label));
  });
}

interface Props {
  text?: string | null;
}

export const ProfileSummary: React.FC<Props> = ({ text }) => {
  const classes = useStyles();
  const sections = getKeyProfileSections(parseProfilePreviewSections(text));

  if (sections.length === 0) {
    return (
      <Typography variant="body2" className={classes.text}>
        {text}
      </Typography>
    );
  }

  return (
    <Box className={classes.summary}>
      {sections.map((section, index) => (
        <Box className={classes.section} key={index}>
          <Typography className={classes.label}>{section.label}</Typography>
          <Typography variant="body2" className={classes.text}>
            {section.text}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};
